import { castArray, head, last, orderBy, tail } from "lodash";
import { getSnapshot, Instance, types } from "mobx-state-tree";
import { ChatItemAssertion } from ".";
import { IAttachment } from "@fidget/common/components/mstchat/chatMessage";
import { logger } from "@fidget/common/utils";
import { createUndeliveredId, createPendingId } from "@fidget/common/utils/chatStoreUtils";
import { getMidnightTimestamp } from "@fidget/common/utils/timeFormatter";
import { currentUserStoreGetter } from "../../utils";
import { ChatItem, IChatItem } from "./ChatItem";
import { ChatItemStatus } from "./ChatItemStatus";
import { ChatItemType } from "./ChatItemType";
import { DateSeparatorChatItem, IUserTextChatItem, UserTextChatItem } from "./items";
import { ChatItemGroup, isChatItemGroupable } from "./items/ChatItemGroup";

const ChatMark = types.model("ChatMark", {
  user: types.identifier,
  mark: types.number
});

export const Chat = types
  .model("Chat", {
    chatItems: types.array(ChatItem),
    pendingChatItems: types.array(UserTextChatItem),
    undeliveredChatItems: types.array(UserTextChatItem),
    oldestTimestamp: types.maybe(types.number),
    historyIsFullyLoaded: false,
    marks: types.map(ChatMark),
    chatScrolledToBottom: false,
    isLoading: false,
    hasFake: false
  })
  .views(currentUserStoreGetter)
  .views((self) => {
    const views = {
      /**
       * Entire (sent + pending) chat history with items grouped where applicable
       */
      get chatEntries() {
        return [
          ...views.groupedChatItems,
          ...views.groupedPendingChatItems,
          ...views.groupedUndeliveredChatItems
        ];
      },

      /**
       * Sent items with grouping
       */
      get groupedChatItems() {
        return self.chatItems.reduce((acc: Array<IChatItem>, item: IChatItem) => {
          if (item.shown) {
            if (isChatItemGroupable(item)) {
              const lastItem = last(acc);
              const added = lastItem && ChatItemAssertion.isGroup(lastItem) && lastItem.add(item);
              if (!added) {
                acc.push(new ChatItemGroup(item));
              }
            } else {
              acc.push(item);
            }
          }
          return acc;
        }, []);
      },

      /**
       * Pending items as chat item group
       */
      get groupedPendingChatItems() {
        const firstItem = head(self.pendingChatItems);
        if (firstItem) {
          const group = new ChatItemGroup(firstItem, Number.MAX_SAFE_INTEGER);
          // FIXME: suspicious assumption - all pending items are mutually groupable?
          tail(self.pendingChatItems).forEach((item) => group.add(item));
          return [group];
        }
        return [];
      },

      get groupedUndeliveredChatItems() {
        return self.undeliveredChatItems.map((item) => new ChatItemGroup(item));
      },

      get isFullHistoryFetched() {
        return self.historyIsFullyLoaded;
      }
    };
    return views;
  })
  .actions((self) => {
    const addUndeliveredMessage = (pending: IUserTextChatItem) => {
      const id = createUndeliveredId(pending.id);

      const undelivered = { ...pending, id, status: ChatItemStatus.UNDELIVERED };
      self.undeliveredChatItems.push(undelivered);
    };

    const actions = {
      // TODO: test with widget
      addCustomMessage(message: IChatItem) {
        logger.debug("adding custom message", message);
        actions.addChatItems(message);
      },
      /**
       * Create pending message
       * @param {string}                 text        message text
       * @param {ArrayLike<IAttachment>} attachments optional attachments
       */
      createPendingMessage(
        text: string,
        attachments?: ReadonlyArray<IAttachment>
      ): IUserTextChatItem {
        const timestamp = Date.now();
        const id = createPendingId(timestamp);
        const user = self.currentUserStore.id!;
        const att = attachments && attachments.map((item) => ({ ...item })); // map mobx attachment to its values
        const pendingItem = UserTextChatItem.create({
          type: ChatItemType.UserText,
          id,
          timestamp,
          text,
          user,
          attachments: att
        });
        logger.debug("adding pending message", pendingItem);
        self.pendingChatItems.push(pendingItem);
        return last(self.pendingChatItems) as IUserTextChatItem;
      },

      /**
       * Handle pending message after it's been sent
       * Since the message comes back as WS event, we just remove it from pending items.
       * @param {string} pendingMessageId identifier for chat item node
       */
      pendingMessageSent(pendingMessageId: string) {
        const pending = self.pendingChatItems.find((value) => value.id === pendingMessageId);
        if (pending) {
          self.pendingChatItems.remove(pending);
        }
      },

      /**
       * Remove message from pending and add it as undelivered
       */
      pendingMessageFailed(pendingMessageId: string) {
        const pending = self.pendingChatItems.find((value) => value.id === pendingMessageId);
        if (pending) {
          self.pendingChatItems.remove(pending);
          addUndeliveredMessage(getSnapshot<IUserTextChatItem>(pending));
        }
      },

      allPendingFailed() {
        self.pendingChatItems.forEach((pending) => {
          self.pendingChatItems.remove(pending);
          addUndeliveredMessage(getSnapshot<IUserTextChatItem>(pending));
        });
      },

      removeUndeliveredMessage(messageId: string): IChatItem | undefined {
        const id = createUndeliveredId(messageId);
        const undelivered = self.undeliveredChatItems.find((value) => value.id === id);
        if (undelivered) {
          self.undeliveredChatItems.remove(undelivered);
          return undelivered;
        }

        return;
      },

      setReadUntilMark(userId: string, timestamp: number) {
        self.chatItems.forEach((item: IChatItem) => {
          if (
            ChatItemAssertion.isUserText(item) &&
            item.user.id === userId &&
            item.timestamp <= timestamp
          ) {
            item.status = ChatItemStatus.READ;
          }
        });
      },
      addChatItems(items: IChatItem[] | IChatItem) {
        items = castArray(items);
        const existingItemIds = self.chatItems.map((item) => item.id);
        // exclude existing items (eg. when a new message goes back in WS event)
        items = items.filter((item: IChatItem) => !existingItemIds.includes(item.id));

        if (items.length) {
          // get a set of unique dates in the new batch
          const dates = new Set(
            items.map((item: IChatItem) => getMidnightTimestamp(item.timestamp))
          );
          // construct array of DateSeparatorChatItem snapshots (filtering out existing dates)
          const separators = [...dates]
            .filter((ts) => !existingItemIds.includes("date-" + ts))
            .map((ts) =>
              DateSeparatorChatItem.create({
                id: "date-" + ts,
                type: ChatItemType.DateSeparator,
                timestamp: ts
              })
            );
          // create a new array from existing items, new items and new separators, sort it by timestamp
          const newItems = orderBy(
            [...self.chatItems, ...items, ...separators],
            ["timestamp"],
            ["asc"]
          );
          // insert into tree
          self.chatItems.replace(newItems);
        }
      },

      reloadChatItems(items: IChatItem[] | IChatItem) {
        self.chatItems.clear();
        actions.addChatItems(items);
      },

      /**
       * Update existing message eg. from a message update event
       * @param {string} id        id of message (ChatItem)
       * @param {string} authorId  author id (in case it changes)
       * @param {any}    eventData data to update with
       */
      updateMessage(id: string, authorId: string, eventData: any) {
        const chatItem = self.chatItems.find((item) => item.id === id);
        if (chatItem) {
          chatItem.updateFromEvent(eventData);
        }
      }
    };
    return actions;
  });

export type IChat = Instance<typeof Chat>;
